import * as React from 'react'
import {format} from 'date-fns'
import {Dialog, DialogDismiss, DialogHeading} from '@ariakit/react'
import * as dialogStyles from '@frontrow/console/src/components/dialog/dialog.module.scss'
import {useReactTable, getCoreRowModel, flexRender, type ColumnDef} from '@tanstack/react-table'
import {Button} from '@frontrow/console/src/components/button'
import {
  capitalizeFirstLetter,
  snakeCaseToSpaces
} from '@frontrow/console/src/entities/helpers/string-helper'
import {type LicenseT} from '@frontrow/console/src/entities/models/license'
import {useModalForm} from '@frontrow/console/src/hooks/use-modal-form'

type Props = {
  expiredLicenses: Array<LicenseT>
  schoolName: string
}

const columnDefs: ColumnDef<LicenseT>[] = [
  {
    id: 'license.startAt',
    header: 'Start',
    accessorFn: license => format(license.startsAt, 'MM/dd/yyyy')
  },
  {
    id: 'license.expiresAt',
    header: 'End',
    accessorFn: license => format(license.expiresAt, 'MM/dd/yyyy')
  },
  {
    id: 'license.subject',
    header: 'Subject',
    accessorFn: license =>
      `${capitalizeFirstLetter(snakeCaseToSpaces(license.subject))} ${license.count}`
  }
]

export default function ExpiredLicensesButtonModal(props: Props): React.ReactElement {
  const {expiredLicenses, schoolName} = props
  const {isOpen, open, close} = useModalForm()

  const table = useReactTable({
    data: expiredLicenses,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel()
  })

  return (
    <React.Fragment>
      <Button style="link" onClick={open}>
        View Expired Licenses ({expiredLicenses.length})
      </Button>
      <Dialog
        open={isOpen}
        onClose={close}
        backdrop={<div className={dialogStyles.backdrop} />}
        className={dialogStyles.dialog}
        portal
        unmountOnHide
        hideOnEscape
        hideOnInteractOutside={false}
      >
        <div className={dialogStyles.header}>
          <DialogHeading className={dialogStyles.title}>
            {schoolName} Expired Licenses
          </DialogHeading>
          <DialogDismiss className={dialogStyles.closeButton}>
            <span aria-hidden="true">&times;</span>
          </DialogDismiss>
        </div>
        <div className={dialogStyles.body}>
          <table>
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th key={header.id}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map(row => (
                <tr key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Dialog>
    </React.Fragment>
  )
}
